import React, { createContext, useContext, useCallback, useEffect, ReactNode } from "react";
import {
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  type ToastIntent,
  ToastTrigger
} from "@fluentui/react-components";
import { toastService } from "../services/ToastService";
import { DismissRegular } from "@fluentui/react-icons";
import notificationStore from "@/stores/notification";

export interface ToastOptions {
  title?: string;
  message: string | ReactNode;
  intent?: ToastIntent;
  timeout?: number;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
  showErrorToast: (message: string | ReactNode, title?: string) => void;
  showSuccessToast: (message: string | ReactNode, title?: string) => void;
  showWarningToast: (message: string | ReactNode, title?: string) => void;
  showInfoToast: (message: string | ReactNode, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const toasterId = "main-toaster";
  const { dispatchToast } = useToastController(toasterId);

  const showToast = useCallback(
    (options: ToastOptions) => {
      const { title, message, intent = "info", timeout = 5000 } = options;

      dispatchToast(
        <Toast>
          {title && (
            <ToastTitle
              action={
                <ToastTrigger>
                  <DismissRegular style={{ cursor: "pointer" }} />
                </ToastTrigger>
              }
            >
              {title}
            </ToastTitle>
          )}
          <ToastBody>{message}</ToastBody>
        </Toast>,
        { intent, timeout }
      );
    },
    [dispatchToast]
  );

  const showErrorToast = useCallback(
    (message: string | ReactNode, title?: string) => {
      showToast({
        title: title || "Error",
        message,
        intent: "error",
        timeout: 8000
      });

      notificationStore.getState().addNotification({
        title: title || "Error",
        message,
        type: "error"
      });
    },
    [showToast]
  );

  const showSuccessToast = useCallback(
    (message: string | ReactNode, title?: string) => {
      showToast({
        title: title || "Success",
        message,
        intent: "success",
        timeout: 4000
      });

      notificationStore.getState().addNotification({
        title: title || "Success",
        message,
        type: "success"
      });
    },
    [showToast]
  );

  const showWarningToast = useCallback(
    (message: string | ReactNode, title?: string) => {
      showToast({
        title: title || "Warning",
        message,
        intent: "warning",
        timeout: 6000
      });

      notificationStore.getState().addNotification({
        title: title || "Warning",
        message,
        type: "warning"
      });
    },
    [showToast]
  );

  const showInfoToast = useCallback(
    (message: string | ReactNode, title?: string) => {
      showToast({
        title: title || "Info",
        message,
        intent: "info",
        timeout: 5000
      });

      notificationStore.getState().addNotification({
        title: title || "Info",
        message,
        type: "info"
      });
    },
    [showToast]
  );

  // Initialize the toast service with our toast functions
  useEffect(() => {
    toastService.init({
      showErrorToast,
      showSuccessToast,
      showWarningToast,
      showInfoToast
    });
  }, [showErrorToast, showSuccessToast, showWarningToast, showInfoToast]);

  const contextValue: ToastContextType = {
    showToast,
    showErrorToast,
    showSuccessToast,
    showWarningToast,
    showInfoToast
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Toaster toasterId={toasterId} position='top-end' />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
